import React, { useEffect, useState } from 'react';
import { DashHeader, Footer, LoadingOverlay, Toast } from '../components';
import { useAuth } from '../contexts/AuthContext';
import FadeIn from 'react-fade-in/lib/FadeIn';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { employerInfoQuery } from '../utils/Data';
import { clientNoCdn } from '../clientNoCdn';
import {
  UserCircleIcon,
  GlobeAltIcon,
  LocationMarkerIcon,
  InformationCircleIcon,
  PhoneIcon,
  CheckCircleIcon,
  MailIcon,
} from '@heroicons/react/solid';
import { useNavigate, Navigate } from 'react-router-dom';
import blockTools, { normalizeBlock } from '@sanity/block-tools';
import Schema from '@sanity/schema';
import axios from 'axios';
import { toHTML } from '@portabletext/to-html';

function Profile() {
  // Context
  const { currentUser, signout, resetPassword } = useAuth();
  const navigate = useNavigate();

  // Current States
  const [employerInfo, setEmployerInfo] = useState({
    _id: '',
    name: '',
    email: '',
    website: '',
    contact: '',
    fullAddress: '',
    bio: [],
  });
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showPasswordErrorToast, setShowPasswordErrorToast] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');

  // Retrieve Current employer Information
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [web, setWeb] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [reminders, setReminders] = useState(true); // REV-004 opt-out
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    clientNoCdn
      .fetch(employerInfoQuery(currentUser.uid))
      .then((res) => {
        setEmployerInfo(res[0]);
        const employerDetails = res[0];
        setName(employerDetails.name);
        setWeb(employerDetails.website);
        setPhone(employerDetails.contact);
        setAddress(employerDetails.fullAddress);
        setBio(toHTML(employerDetails.bio));
        setReminders(employerDetails.emailRemindersOff !== true);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [currentUser]);

  // Updated States

  const handleBio = (e) => {
    setBio(e);
  };

  // Signout handle
  function handleSignout(e) {
    try {
      signout();
      navigate('/');
    } catch {
      setShowErrorModal(true);
    }
  }

  // ChangePassBtnClick
  function changePassBtnClick() {
    try {
      resetPassword(currentUser.email);
      setShowPasswordModal(true);
    } catch (err) {
      setPasswordErrorMessage(err.message || 'Failed to send password reset email.');
      setShowPasswordErrorToast(true);
    }
  }

  // Push Block contents
  const defaultSchema = Schema.compile({
    name: 'myBlog',
    types: [
      {
        type: 'object',
        name: 'blogPost',
        fields: [
          {
            title: 'Title',
            type: 'string',
            name: 'title',
          },
          {
            title: 'Body',
            name: 'body',
            type: 'array',
            of: [{ type: 'block' }],
          },
        ],
      },
    ],
  });

  const blockContentType = defaultSchema
    .get('blogPost')
    .fields.find((field) => field.name === 'body').type;

  const blocks = blockTools.htmlToBlocks(bio, blockContentType);
  const readyBlocks = normalizeBlock(blocks, {
    allowedDecorators: ['strong', 'em'],
  });

  async function updateProfile(e) {
    setLoading(true);
    e.preventDefault();
    const employer = {
      employerId: currentUser.uid,
      name: name,
      website: web,
      contact: phone,
      fullAddress: address,
      bio: readyBlocks,
      emailRemindersOff: !reminders,
    };

    await axios
      .post('/.netlify/functions/updateProfile', employer)
      .then((response) => {
        setShowSuccessModal(true);
        console.log('Success, Profile Updated!');
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
      });
  }

  // *************************************************************

  if (!currentUser && !loading) {
    return <Navigate to="/sign-in" replace />;
  }

  if (loading) {
    return <LoadingOverlay />;
  }

  if (!currentUser) {
    return <Navigate to="/sign-in" replace />;
  }

  return (
    <>
      <DashHeader handle={handleSignout} userEmail={currentUser?.email} />
      <div className="px-4 mx-4 flex flex-col sm:flex-row justify-center lg:justify-end items-center mt-4 mb-8">
        <div className="flex sm:flex-row flex-col">
          <button
            onClick={changePassBtnClick}
            className="px-4 my-1 py-1 mx-2 bg-white font-semibold text-primary border-2 border-primary hover:text-white hover:bg-primary ease-in duration-75 rounded-md"
          >
            Change Password
          </button>
        </div>
      </div>
      <div className="sm:px-4 sm:mx-4 xl:px-32 xl:mx-32 py-2 sm:py-16">
        <div className="mt-10 sm:mt-0">
          <div className="xl:grid xl:grid-cols-3 xl:gap-6">
            <div className="md:col-span-1">
              <div className="px-4 sm:px-0">
                <h3 className="text-lg text-center xl:text-left font-bold text-primary leading-6">
                  Profile Settings
                </h3>
                <p className="mt-1 text-center xl:text-left text-sm text-gray-900">
                  Update your company information and bio.
                </p>
              </div>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <form onSubmit={updateProfile}>
                <div className="shadow overflow-hidden sm:rounded-md">
                  <div className="px-4 py-5 bg-white sm:p-6">
                    <div className="grid grid-cols-6 gap-6">
                      <div className="col-span-6">
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Company Name
                        </label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          required
                          className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          placeholder="Company Name"
                          value={name}
                          onInput={(e) => setName(e.target.value)}
                        />
                      </div>

                      <div className="col-span-6 sm:col-span-3">
                        <label
                          htmlFor="web"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Website <span className="text-gray-500">(optional)</span>
                        </label>
                        <input
                          id="web"
                          name="web"
                          type="text"
                          className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          placeholder="yourcompany.com"
                          value={web}
                          onInput={(e) => setWeb(e.target.value)}
                        />
                      </div>

                      <div className="col-span-6 sm:col-span-3">
                        <label
                          htmlFor="phone"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Contact Phone <span className="text-gray-500">(optional)</span>
                        </label>
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          placeholder="(123) 456-7890"
                          value={phone}
                          onInput={(e) => setPhone(e.target.value)}
                        />
                      </div>

                      <div className="col-span-6">
                        <label
                          htmlFor="address"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Address
                        </label>
                        <input
                          id="address"
                          name="address"
                          type="text"
                          required
                          className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          placeholder="Full Address"
                          value={address}
                          onInput={(e) => setAddress(e.target.value)}
                        />
                      </div>

                      <div className="col-span-6">
                        <label
                          htmlFor="bio"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Company Bio
                        </label>
                        <div className="mt-1">
                          <ReactQuill
                            theme="snow"
                            placeholder="Tell candidates about your company..."
                            value={bio}
                            onChange={handleBio}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <label className="mt-4 ml-4 sm:ml-0 flex cursor-pointer items-start text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                    checked={reminders}
                    onChange={(e) => setReminders(e.target.checked)}
                  />
                  <span>
                    <span className="font-medium">Email me before my postings expire</span>
                    <span className="block text-gray-500">A reminder 7 days before and on the last day, with a one-click Extend link.</span>
                  </span>
                </label>
                <button
                  type="submit"
                  className="bg-primary ml-4 sm:ml-0 px-4 py-1 my-4 rounded text-white text-lg flex items-center hover:bg-red-800"
                >
                  Update Profile
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowPasswordModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <MailIcon className="h-6 w-6 text-primary" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Password Reset Email Sent
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        We've sent a password reset link to <span className="font-semibold">{currentUser.email}</span>. Please check your inbox and follow the instructions to reset your password.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Update Success Modal */}
      {showSuccessModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowSuccessModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <CheckCircleIcon className="h-6 w-6 text-primary" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Profile Updated Successfully
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Your profile has been updated. Changes may take a few seconds to appear.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowSuccessModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message="Unable to sign out. Please try again."
        type="error"
      />

      <Toast
        isOpen={showPasswordErrorToast}
        onClose={() => setShowPasswordErrorToast(false)}
        message={passwordErrorMessage}
        type="error"
      />

      <Footer />
    </>
  );
}

export default Profile;
